import { useEffect } from "react";
import { Modal, Form, Input } from "antd";
import {
  Role,
  CreateRolePayload,
  UpdateRolePayload,
} from "@/utils/types/tenantPermissions";
import { TenantRoleService } from "@/services/APIs";
import { notify } from "@/components/common/Notification";

interface RoleModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  initialData?: Role | null;
  isEditMode?: boolean;
}

const RoleModal: React.FC<RoleModalProps> = ({
  open,
  onCancel,
  onSuccess,
  initialData,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (isEditMode && initialData) {
        form.setFieldsValue({
          name: initialData.name,
          description: initialData.description ?? "",
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialData, isEditMode, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (isEditMode && initialData) {
        const payload: UpdateRolePayload = {
          name: values.name,
          description: values.description ?? undefined,
        };
        await TenantRoleService.updateRole(initialData.id, payload);
        notify.success("Role updated successfully");
      } else {
        const payload: CreateRolePayload = {
          name: values.name,
          description: values.description ?? undefined,
        };
        await TenantRoleService.createRole(payload);
        notify.success("Role created successfully");
      }

      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation errors
        return;
      }
      notify.error(error?.message ?? "Failed to save role");
    }
  };

  return (
    <Modal
      title={isEditMode ? "Edit Role" : "Add Role"}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={isEditMode ? "Update" : "Create Role"}
      cancelText="Cancel"
      destroyOnClose
      width={500}
      maskClosable={false}
      centered
      okButtonProps={{
        style: {
          backgroundColor: "#0c3055",
          borderColor: "#0c3055",
        },
      }}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="name"
          label="Role Name"
          rules={[
            { required: true, message: "Role name is required" },
            { max: 100, message: "Role name must be less than 100 characters" },
          ]}
        >
          <Input placeholder="Enter role name" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            {
              max: 500,
              message: "Description must be less than 500 characters",
            },
          ]}
        >
          <Input.TextArea
            placeholder="Enter role description"
            rows={4}
            showCount
            maxLength={500}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RoleModal;
